/**
 * ExplorePage — Scrollable Discovery Feed with Fork Stacks
 *
 * Projects appear chronologically. When a project has forks (descendants),
 * its card becomes a "stack" — the root project is on top, with visual
 * depth cues showing more cards behind it. Users swipe left/right (mobile)
 * or click arrows (desktop) to browse the full lineage chain.
 *
 * Lineage chains are built client-side from parent_project_id /
 * parent_project_ids fields. Only root projects (those that aren't
 * descendants of another project in the current dataset) appear as
 * top-level feed items. All their descendants are folded into their stack.
 *
 * Cards adapt to content:
 * - Projects WITH cover images → visual card (image dominant)
 * - Projects WITHOUT covers → text card (description + tags prominent)
 *
 * Layout: 1 col mobile → 2 col tablet → 3 col desktop
 *
 * v11 Changes:
 * - Replaced local search bar + zone chips + collapsible filters with
 *   UnifiedSearchBar component (reads from PlatformContext)
 * - Removed duplicate sticky header block that caused JSX parse error
 * - Filters now clearable from Explore via pill X buttons
 * - Search/filter appearance identical to Map page
 *
 * v12 Changes:
 * - REMOVED local activeZone/activeType/activeSort useState — now reads
 *   from platform.filters (zone, type, sort persist Map ↔ Explore)
 * - REMOVED local fetchProjects/fetchUsers/fetchChallenges — projects come
 *   from platform.filters.applyFilters(globalProjects) using the shared cache
 * - REMOVED duplicated buildExploreLineage — now uses
 *   platform.lineage.buildLineageChain (single source of truth)
 * - Inline search results (People, Challenges) now read from platformSearch
 *   enriched results instead of local API calls
 */

import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, GitFork, Eye, Clock,
  X, Package, Palette, Music, Cpu,
  BookOpen, DollarSign, Heart, Stethoscope,
  ArrowRight, Target, Users, Sparkles,
  ChevronLeft, ChevronRight, Map as MapIcon,
  SlidersHorizontal, LayoutGrid, Layers, Monitor,
  TrendingUp, Utensils, ArrowLeft, Zap, FileText,
  MessageSquare, ExternalLink, AlertCircle, CheckSquare
} from 'lucide-react';
import { cn } from '../utils/cn';
import FeedSelectionBar, { useFeedSelection } from '../components/FeedSelectionBar';
import { api, projectsAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { useTray } from '../context/TrayContext';
import { usePlatform } from '../context/PlatformContext';
import { ZONES, PROJECT_TYPES, getProjectZone } from '../config/zones';
import ProjectDetailOverlay from '../components/overlays/ProjectDetailOverlay';
import CreateChallengeOverlay from '../components/overlays/CreateChallengeOverlay';
import OverlayErrorBoundary from '../components/overlays/OverlayErrorBoundary';
import UnifiedSearchBar from '../components/UnifiedSearchBar';

// ============================================================================
// CONSTANTS
// ============================================================================

const ZONE_LIST = Object.values(ZONES);

const ZONE_ICONS = {
  build: Package,
  beautiful: Palette,
  sound: Music,
  taste: Utensils,
  story: BookOpen,
  heal: Stethoscope,
  smart: Cpu,
  sell: TrendingUp,
};

const SORT_OPTIONS = [
  { id: 'surprising', label: 'Surprising', icon: Sparkles },
  { id: 'newest', label: 'Newest', icon: Clock },
  { id: 'forks', label: 'Most Forked', icon: GitFork },
  { id: 'deepest', label: 'Deepest Lineage', icon: Layers },
];

const TYPE_OPTIONS = [
  { id: 'all', label: 'All', icon: LayoutGrid },
  { id: 'physical', label: 'Physical', icon: Package, color: PROJECT_TYPES.physical.color },
  { id: 'digital', label: 'Digital', icon: Monitor, color: PROJECT_TYPES.digital.color },
  { id: 'hybrid', label: 'Hybrid', icon: Layers, color: PROJECT_TYPES.hybrid.color },
];

const PER_PAGE = 24;

// ============================================================================
// HELPERS
// ============================================================================

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
  if (seconds < 2592000) return `${Math.floor(seconds / 604800)}w`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const fmt = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
};

const getZoneForProject = (project) => {
  try { return getProjectZone(project); } catch { return ZONES.smart; }
};

const getTags = (project) =>
  (project.tags || []).map(t => typeof t === 'string' ? t : t.name || '').filter(Boolean);

const getCover = (project) =>
  project.cover_image_url || project.coverImageUrl || project.image_url || project.imageUrl;

const getCreator = (project) =>
  project.creator_username || project.creatorUsername || project.author?.username || project.username || 'Unknown';

/**
 * Build lineage stacks from a flat array of projects.
 */
function buildLineageStacks(projects) {
  const byId = new Map();
  projects.forEach(p => byId.set(p.id, p));

  const childrenOf = new Map();

  projects.forEach(p => {
    const parentIds = [];
    const singleParent = p.parent_project_id || p.parentProjectId;
    const multiParents = p.parent_project_ids || p.parentProjectIds || [];

    if (multiParents.length > 0) {
      parentIds.push(...multiParents);
    } else if (singleParent) {
      parentIds.push(singleParent);
    }

    parentIds.forEach(pid => {
      if (byId.has(pid)) {
        if (!childrenOf.has(pid)) childrenOf.set(pid, []);
        childrenOf.get(pid).push(p);
      }
    });
  });

  const isChild = new Set();
  projects.forEach(p => {
    const parentIds = [];
    const singleParent = p.parent_project_id || p.parentProjectId;
    const multiParents = p.parent_project_ids || p.parentProjectIds || [];

    if (multiParents.length > 0) parentIds.push(...multiParents);
    else if (singleParent) parentIds.push(singleParent);

    if (parentIds.some(pid => byId.has(pid))) {
      isChild.add(p.id);
    }
  });

  function collectDescendants(rootId) {
    const chain = [];
    const visited = new Set([rootId]);
    const queue = [rootId];

    while (queue.length > 0) {
      const current = queue.shift();
      const children = childrenOf.get(current) || [];
      children.sort((a, b) => {
        const aDate = a.created_at || a.createdAt || '';
        const bDate = b.created_at || b.createdAt || '';
        return new Date(aDate) - new Date(bDate);
      });
      for (const child of children) {
        if (!visited.has(child.id)) {
          visited.add(child.id);
          chain.push(child);
          queue.push(child.id);
        }
      }
    }
    return chain;
  }

  const stacks = [];
  const consumed = new Set();

  projects.forEach(p => {
    if (consumed.has(p.id)) return;
    if (isChild.has(p.id)) return;

    const descendants = collectDescendants(p.id);
    const chain = [p, ...descendants];

    chain.forEach(c => consumed.add(c.id));

    stacks.push({
      root: p,
      chain,
      hasStack: chain.length > 1,
    });
  });

  projects.forEach(p => {
    if (!consumed.has(p.id)) {
      consumed.add(p.id);
      const descendants = collectDescendants(p.id);
      const chain = [p, ...descendants];
      chain.forEach(c => consumed.add(c.id));
      stacks.push({ root: p, chain, hasStack: chain.length > 1 });
    }
  });

  stacks.forEach(stack => {
    const crossZones = [];
    stack.chain.forEach((proj, i) => {
      if (i > 0) {
        const prevZ = getZoneForProject(stack.chain[i - 1]);
        const currZ = getZoneForProject(proj);
        if (prevZ?.id && currZ?.id && prevZ.id !== currZ.id) {
          crossZones.push({ from: prevZ, to: currZ, fromProject: stack.chain[i - 1], toProject: proj });
        }
      }
    });
    stack.crossZones = crossZones;
    stack.lineageDepth = stack.chain.length;
  });

  return stacks;
}

// ============================================================================
// CROSS-ZONE BADGE
// ============================================================================

const CrossZoneBadge = ({ crossZones, className }) => {
  if (!crossZones || crossZones.length === 0) return null;
  const transition = crossZones[0];
  const fromColor = transition.from?.color || '#71717a';
  const toColor = transition.to?.color || '#71717a';

  return (
    <div className={cn(
      'flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold',
      'backdrop-blur-md border',
      className
    )} style={{
      background: `linear-gradient(135deg, ${fromColor}20, ${toColor}20)`,
      borderColor: `${fromColor}30`,
    }}>
      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: fromColor }} />
      <Zap className="w-2.5 h-2.5 text-zinc-400" />
      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: toColor }} />
      {crossZones.length > 1 && (
        <span className="text-zinc-500 ml-0.5">+{crossZones.length - 1}</span>
      )}
    </div>
  );
};

// ============================================================================
// LINEAGE DEPTH INDICATOR
// ============================================================================

const LineageDepthIndicator = ({ depth }) => {
  if (depth <= 1) return null;
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-px">
        {Array.from({ length: Math.min(depth, 6) }).map((_, i) => (
          <div
            key={i}
            className="w-1 rounded-full"
            style={{
              height: `${6 + i * 2}px`,
              backgroundColor: '#a855f7',
              opacity: 0.4 + (i / Math.max(depth, 1)) * 0.6,
            }}
          />
        ))}
        {depth > 6 && <span className="text-[9px] text-zinc-600 ml-0.5">+{depth - 6}</span>}
      </div>
      <span className="text-[10px] text-zinc-600">{depth} gens</span>
    </div>
  );
};

// ============================================================================
// SURPRISING CONNECTION SPOTLIGHT
// ============================================================================

const SurprisingConnectionSpotlight = ({ stacks, onView, onViewOnMap }) => {
  const spotlights = useMemo(() => {
    return stacks
      .filter(s => s.crossZones && s.crossZones.length > 0 && s.hasStack)
      .sort((a, b) => {
        const scoreA = a.crossZones.length * a.lineageDepth * Math.max(1, a.root.fork_count || 0);
        const scoreB = b.crossZones.length * b.lineageDepth * Math.max(1, b.root.fork_count || 0);
        return scoreB - scoreA;
      })
      .slice(0, 6);
  }, [stacks]);

  if (!spotlights.length) return null;

  return (
    <div className="mt-6 mb-6">
      <h2 className="text-[13px] font-semibold text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-purple-400" />
        Surprising Connections
      </h2>

      {/* Mobile: horizontal scroll */}
      <div className="sm:hidden flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide snap-x snap-mandatory">
        {spotlights.map((spotlight) => {
          const { root, chain, crossZones } = spotlight;
          const rootZone = getZoneForProject(root);
          const lastProject = chain[chain.length - 1];
          const lastZone = getZoneForProject(lastProject);
          const rootCover = getCover(root);
          const lastCover = getCover(lastProject);

          return (
            <div
              key={root.id}
              onClick={() => onView(root)}
              className="flex-shrink-0 w-[280px] snap-start bg-zinc-900/60 border border-zinc-800/50 rounded-2xl overflow-hidden cursor-pointer transition-all active:scale-[0.97]"
            >
              <div className="flex h-28">
                <div className="w-1/2 relative overflow-hidden">
                  {rootCover ? (
                    <img src={rootCover} alt={root.name} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${rootZone?.color}12, transparent)` }}>
                      <span className="text-2xl font-bold" style={{ color: `${rootZone?.color}15` }}>{root.name?.[0]?.toUpperCase() || '?'}</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-zinc-900/50" />
                  <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold backdrop-blur-md" style={{ backgroundColor: `${rootZone?.color}30`, color: rootZone?.color }}>
                    {rootZone?.shortLabel}
                  </div>
                </div>
                <div className="absolute left-1/2 top-14 -translate-x-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-zinc-900 border border-zinc-700/50 flex items-center justify-center">
                  <Zap className="w-3 h-3 text-purple-400" />
                </div>
                <div className="w-1/2 relative overflow-hidden">
                  {lastCover ? (
                    <img src={lastCover} alt={lastProject.name} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${lastZone?.color}12, transparent)` }}>
                      <span className="text-2xl font-bold" style={{ color: `${lastZone?.color}15` }}>{lastProject.name?.[0]?.toUpperCase() || '?'}</span>
                    </div>
                  )}
                  <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold backdrop-blur-md" style={{ backgroundColor: `${lastZone?.color}30`, color: lastZone?.color }}>
                    {lastZone?.shortLabel}
                  </div>
                </div>
              </div>
              <div className="px-3 py-2.5">
                <p className="text-[11px] text-zinc-300 font-semibold line-clamp-1">{root.name}</p>
                <div className="flex items-center gap-1.5 mt-1 text-[10px] text-zinc-500">
                  <span className="flex items-center gap-0.5"><Layers className="w-2.5 h-2.5" />{chain.length} gens</span>
                  <span className="flex items-center gap-0.5 text-purple-400"><Zap className="w-2.5 h-2.5" />{crossZones.length} leap{crossZones.length !== 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop: large feature card */}
      <div className="hidden sm:block">
        {(() => {
          const spotlight = spotlights[0];
          if (!spotlight) return null;
          const { root, chain, crossZones } = spotlight;
          const coverImage = getCover(root);
          const rootZone = getZoneForProject(root);
          const lastProject = chain[chain.length - 1];
          const lastZone = getZoneForProject(lastProject);
          const lastCover = getCover(lastProject);

          return (
            <div
              onClick={() => onView(root)}
              className="group relative bg-zinc-900/50 border border-zinc-800/50 rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:border-purple-500/30 hover:shadow-xl hover:shadow-purple-500/5"
            >
              <div className="flex flex-col sm:flex-row">
                <div className="relative sm:w-1/2 aspect-[16/9] sm:aspect-auto overflow-hidden">
                  {coverImage ? (
                    <img src={coverImage} alt={root.name} className="w-full h-full object-cover min-h-[140px]" loading="lazy" />
                  ) : (
                    <div className="w-full h-full min-h-[140px] flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${rootZone?.color}15, transparent)` }}>
                      <span className="text-5xl font-bold select-none" style={{ color: `${rootZone?.color}15` }}>
                        {root.name?.[0]?.toUpperCase() || '?'}
                      </span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 via-zinc-950/20 to-transparent sm:bg-gradient-to-r sm:from-transparent sm:via-transparent sm:to-zinc-900/80" />
                  <div className="absolute bottom-3 left-3">
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold backdrop-blur-md mb-1.5 w-fit"
                      style={{ backgroundColor: `${rootZone?.color}25`, color: rootZone?.color }}>
                      {rootZone?.shortLabel}
                    </div>
                    <p className="text-white font-semibold text-sm line-clamp-1">{root.name}</p>
                    <p className="text-zinc-400 text-[11px]">{getCreator(root)}</p>
                  </div>
                </div>

                <div className="flex flex-col items-center justify-center px-4 py-6 relative -mx-3 z-10">
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: rootZone?.color }} />
                    <div className="w-px h-6" style={{ background: `linear-gradient(to bottom, ${rootZone?.color}, ${lastZone?.color})` }} />
                    <Zap className="w-4 h-4 text-purple-400" />
                    <div className="w-px h-6" style={{ background: `linear-gradient(to bottom, ${rootZone?.color}, ${lastZone?.color})` }} />
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: lastZone?.color }} />
                  </div>
                  <p className="text-[9px] text-zinc-600 mt-2 whitespace-nowrap">{chain.length} generations</p>
                </div>

                <div className="relative sm:w-1/2 aspect-[16/9] sm:aspect-auto overflow-hidden">
                  {lastCover ? (
                    <img src={lastCover} alt={lastProject.name} className="w-full h-full object-cover min-h-[140px]" loading="lazy" />
                  ) : (
                    <div className="w-full h-full min-h-[140px] flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${lastZone?.color}15, transparent)` }}>
                      <span className="text-5xl font-bold select-none" style={{ color: `${lastZone?.color}15` }}>
                        {lastProject.name?.[0]?.toUpperCase() || '?'}
                      </span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 via-zinc-950/20 to-transparent sm:bg-gradient-to-l sm:from-transparent sm:via-transparent sm:to-zinc-900/80" />
                  <div className="absolute bottom-3 right-3 text-right">
                    <div className="flex items-center justify-end gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold backdrop-blur-md mb-1.5 ml-auto w-fit"
                      style={{ backgroundColor: `${lastZone?.color}25`, color: lastZone?.color }}>
                      {lastZone?.shortLabel}
                    </div>
                    <p className="text-white font-semibold text-sm line-clamp-1">{lastProject.name}</p>
                    <p className="text-zinc-400 text-[11px]">{getCreator(lastProject)}</p>
                  </div>
                </div>
              </div>

              <div className="px-4 py-2.5 border-t border-zinc-800/30 flex items-center justify-between">
                <div className="flex items-center gap-3 text-[11px] text-zinc-500">
                  <span className="flex items-center gap-1"><Layers className="w-3 h-3" />{chain.length} projects in lineage</span>
                  <span className="flex items-center gap-1 text-purple-400"><Zap className="w-3 h-3" />{crossZones.length} cross-zone leap{crossZones.length !== 1 ? 's' : ''}</span>
                </div>
                <span className="text-[11px] text-purple-400 flex items-center gap-1 group-hover:text-purple-300 transition-colors">
                  Explore lineage <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

// ============================================================================
// CONNECTION LINES
// ============================================================================

const ConnectionLines = ({ projects, gridRef, stacks = [] }) => {
  const svgRef = useRef(null);
  const [lines, setLines] = useState([]);
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });
  const frameRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const projectToRoot = useMemo(() => {
    const map = new Map();
    stacks.forEach(stack => {
      stack.chain.forEach(p => map.set(p.id, stack.root.id));
    });
    return map;
  }, [stacks]);

  const edges = useMemo(() => {
    const rootIds = new Set(stacks.map(s => s.root.id));
    const edgeSet = new Set();
    const result = [];

    projects.forEach(p => {
      const parentIds = [];
      const single = p.parent_project_id || p.parentProjectId;
      const multi = p.parent_project_ids || p.parentProjectIds || [];
      if (multi.length > 0) parentIds.push(...multi);
      else if (single) parentIds.push(single);

      parentIds.forEach(pid => {
        const childRoot = projectToRoot.get(p.id);
        const parentRoot = projectToRoot.get(pid);

        if (childRoot && parentRoot && childRoot !== parentRoot && rootIds.has(childRoot) && rootIds.has(parentRoot)) {
          const key = `${parentRoot}-${childRoot}`;
          if (!edgeSet.has(key)) {
            edgeSet.add(key);

            const parentProject = projects.find(x => x.id === parentRoot) || projects.find(x => x.id === pid);
            const childProject = projects.find(x => x.id === childRoot) || p;
            const parentZone = parentProject ? getZoneForProject(parentProject)?.id : null;
            const childZone = childProject ? getZoneForProject(childProject)?.id : null;

            result.push({
              sourceId: parentRoot,
              targetId: childRoot,
              isCrossZone: parentZone && childZone && parentZone !== childZone,
              sourceZone: parentZone,
              targetZone: childZone,
            });
          }
        }
      });
    });

    return result;
  }, [projects, projectToRoot, stacks]);

  const computeLines = useCallback(() => {
    const grid = gridRef?.current;
    if (!grid || edges.length === 0) {
      setLines([]);
      return;
    }

    const gridRect = grid.getBoundingClientRect();
    setSvgSize({ width: gridRect.width, height: gridRect.height });

    const newLines = [];
    edges.forEach(edge => {
      const sourceEl = grid.querySelector(`[data-project-id="${edge.sourceId}"]`);
      const targetEl = grid.querySelector(`[data-project-id="${edge.targetId}"]`);
      if (!sourceEl || !targetEl) return;

      const sRect = sourceEl.getBoundingClientRect();
      const tRect = targetEl.getBoundingClientRect();

      const sx = sRect.left - gridRect.left + sRect.width / 2;
      const sy = sRect.top - gridRect.top + sRect.height / 2;
      const tx = tRect.left - gridRect.left + tRect.width / 2;
      const ty = tRect.top - gridRect.top + tRect.height / 2;

      const angle = Math.atan2(ty - sy, tx - sx);
      const sEdgeX = sx + Math.cos(angle) * (sRect.width / 2 * 0.7);
      const sEdgeY = sy + Math.sin(angle) * (sRect.height / 2 * 0.6);
      const tEdgeX = tx - Math.cos(angle) * (tRect.width / 2 * 0.7);
      const tEdgeY = ty - Math.sin(angle) * (tRect.height / 2 * 0.6);

      const midX = (sEdgeX + tEdgeX) / 2;
      const midY = (sEdgeY + tEdgeY) / 2;
      const dist = Math.hypot(tEdgeX - sEdgeX, tEdgeY - sEdgeY);
      const sag = Math.min(dist * 0.12, 40);

      const sourceZone = ZONES[edge.sourceZone];
      const targetZone = ZONES[edge.targetZone];

      newLines.push({
        key: `${edge.sourceId}-${edge.targetId}`,
        sEdgeX, sEdgeY, tEdgeX, tEdgeY, cpX: midX, cpY: midY + sag,
        isCrossZone: edge.isCrossZone,
        sourceColor: sourceZone?.color || '#a855f7',
        targetColor: targetZone?.color || '#a855f7',
      });
    });

    setLines(newLines);
  }, [edges, gridRef]);

  useEffect(() => {
    const timer = setTimeout(computeLines, 150);
    return () => clearTimeout(timer);
  }, [computeLines, projects, stacks]);

  useEffect(() => {
    const scrollEl = gridRef?.current?.closest('[class*="overflow-y"]') || window;

    const handleUpdate = () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(computeLines);
    };

    scrollEl.addEventListener('scroll', handleUpdate, { passive: true });
    window.addEventListener('resize', handleUpdate, { passive: true });

    let ro;
    if (gridRef?.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(handleUpdate);
      ro.observe(gridRef.current);
    }

    return () => {
      scrollEl.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
      ro?.disconnect();
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [computeLines, gridRef]);

  if (isMobile || lines.length === 0 || svgSize.width === 0) return null;

  return (
    <svg
      ref={svgRef}
      width={svgSize.width}
      height={svgSize.height}
      className="absolute top-0 left-0 pointer-events-none"
      style={{ zIndex: 1 }}
    >
      <defs>
        <filter id="explore-line-glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {lines.map(line => {
        const path = `M ${line.sEdgeX} ${line.sEdgeY} Q ${line.cpX} ${line.cpY} ${line.tEdgeX} ${line.tEdgeY}`;
        const gradId = `grad-${line.key}`;

        return (
          <g key={line.key}>
            {line.isCrossZone && (
              <defs>
                <linearGradient id={gradId} x1={line.sEdgeX} y1={line.sEdgeY} x2={line.tEdgeX} y2={line.tEdgeY} gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor={line.sourceColor} />
                  <stop offset="100%" stopColor={line.targetColor} />
                </linearGradient>
              </defs>
            )}

            {line.isCrossZone ? (
              <>
                <path d={path} fill="none" stroke={`url(#${gradId})`} strokeWidth={4} strokeOpacity={0.15} filter="url(#explore-line-glow)" />
                <path d={path} fill="none" stroke={`url(#${gradId})`} strokeWidth={1.8} strokeOpacity={0.55} strokeLinecap="round" />
              </>
            ) : (
              <path d={path} fill="none" stroke={line.sourceColor || '#71717a'} strokeWidth={1} strokeOpacity={0.18} strokeLinecap="round" strokeDasharray="6 4" />
            )}

            <circle cx={line.sEdgeX} cy={line.sEdgeY} r={line.isCrossZone ? 3.5 : 2.5} fill={line.sourceColor} fillOpacity={line.isCrossZone ? 0.6 : 0.25} />
            <circle cx={line.tEdgeX} cy={line.tEdgeY} r={line.isCrossZone ? 3.5 : 2.5} fill={line.targetColor} fillOpacity={line.isCrossZone ? 0.6 : 0.25} />
          </g>
        );
      })}
    </svg>
  );
};

// ============================================================================
// VISUAL CARD
// ============================================================================

const VisualCard = ({ project, onView, onViewOnMap, onSelect, isSelected, isInStack, stackPosition, crossZones, lineageDepth }) => {
  const zone = getZoneForProject(project);
  const tags = getTags(project);
  const coverImage = getCover(project);
  const projectType = project.project_type || project.projectType || 'digital';
  const typeConfig = PROJECT_TYPES[projectType];
  const ZoneIcon = ZONE_ICONS[zone.id] || Package;

  return (
    <div
      data-project-id={project.id}
      onClick={() => onView(project)}
      className={cn(
        'group relative bg-zinc-900/50 border rounded-2xl overflow-hidden cursor-pointer transition-all duration-300',
        isSelected ? 'border-purple-500 ring-2 ring-purple-500/30 shadow-lg shadow-purple-500/10' : 'border-zinc-800/50',
        !isInStack && !isSelected && 'hover:border-zinc-700/70 hover:shadow-xl hover:shadow-black/30 hover:-translate-y-0.5',
        isInStack && 'h-full flex flex-col'
      )}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={coverImage}
          alt={project.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          loading="lazy"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 via-transparent to-zinc-950/20" />

        <div
          className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-semibold backdrop-blur-md"
          style={{ backgroundColor: `${zone.color}25`, color: zone.color, border: `1px solid ${zone.color}30` }}
        >
          <ZoneIcon className="w-3 h-3" />
          {zone.shortLabel}
        </div>

        {typeConfig && (
          <div
            className="absolute bottom-3 right-3 w-2.5 h-2.5 rounded-full ring-2 ring-zinc-950/60"
            style={{ backgroundColor: typeConfig.color }}
            title={projectType}
          />
        )}

        {!isInStack && crossZones && crossZones.length > 0 && (
          <CrossZoneBadge crossZones={crossZones} className="absolute top-3 left-3" />
        )}

        {/* Select button (top-right cluster) */}
        {!isInStack && onSelect && (
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(project); }}
            className={cn(
              'absolute top-3 right-12 p-1.5 rounded-lg backdrop-blur-sm transition-all z-10',
              isSelected
                ? 'bg-purple-500 text-white opacity-100'
                : 'bg-zinc-950/50 text-zinc-400 hover:text-white opacity-0 group-hover:opacity-100'
            )}
            title={isSelected ? 'Deselect' : 'Select for fork/collect'}
          >
            <CheckSquare className="w-3.5 h-3.5" />
          </button>
        )}

        {!isInStack && (
          <button
            onClick={(e) => { e.stopPropagation(); onViewOnMap(project); }}
            className="absolute top-3 right-3 p-1.5 rounded-lg bg-zinc-950/50 backdrop-blur-sm opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-white transition-all"
            title="View on Map"
          >
            <MapIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="p-4">
        <h3 className="text-[15px] font-semibold text-white group-hover:text-purple-300 transition-colors line-clamp-1 leading-snug">
          {project.name}
        </h3>
        {project.description && (
          <p className="text-[13px] text-zinc-500 line-clamp-2 leading-relaxed mt-1.5">{project.description}</p>
        )}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {tags.slice(0, 3).map((tag, i) => (
              <span key={i} className="px-2 py-0.5 bg-zinc-800/70 rounded-md text-[11px] text-zinc-500 font-medium">{tag}</span>
            ))}
            {tags.length > 3 && <span className="text-[11px] text-zinc-600 self-center">+{tags.length - 3}</span>}
          </div>
        )}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800/40">
          <span className="text-[12px] text-zinc-500 truncate max-w-[120px]">{getCreator(project)}</span>
          <div className="flex items-center gap-3 text-[12px] text-zinc-600">
            {!isInStack && lineageDepth > 1 && <LineageDepthIndicator depth={lineageDepth} />}
            {(project.fork_count || 0) > 0 && <span className="flex items-center gap-1"><GitFork className="w-3 h-3" />{fmt(project.fork_count)}</span>}
            {(project.parent_project_id || (project.parent_project_ids && project.parent_project_ids.length > 0)) && (
              <span className="flex items-center gap-1 text-purple-500/70"><Layers className="w-3 h-3" />lineage</span>
            )}
            <span>{timeAgo(project.created_at || project.createdAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// TEXT CARD
// ============================================================================

const TextCard = ({ project, onView, onViewOnMap, onSelect, isSelected, isInStack }) => {
  const zone = getZoneForProject(project);
  const tags = getTags(project);
  const projectType = project.project_type || project.projectType || 'digital';
  const typeConfig = PROJECT_TYPES[projectType];
  const ZoneIcon = ZONE_ICONS[zone.id] || Package;

  return (
    <div
      data-project-id={project.id}
      onClick={() => onView(project)}
      className={cn(
        'group relative bg-zinc-900/50 border rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 flex flex-col',
        isSelected ? 'border-purple-500 ring-2 ring-purple-500/30 shadow-lg shadow-purple-500/10' : 'border-zinc-800/50',
        !isInStack && !isSelected && 'hover:border-zinc-700/70 hover:shadow-xl hover:shadow-black/30 hover:-translate-y-0.5',
        isInStack && 'h-full'
      )}
    >
      <div
        className="relative aspect-[16/10] overflow-hidden flex items-center justify-center"
        style={{ background: `linear-gradient(135deg, ${zone.color}08, ${zone.color}03, transparent)` }}
      >
        <span className="text-4xl font-bold select-none" style={{ color: `${zone.color}18` }}>
          {project.name?.[0]?.toUpperCase() || '?'}
        </span>

        <div
          className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-semibold backdrop-blur-md"
          style={{ backgroundColor: `${zone.color}25`, color: zone.color, border: `1px solid ${zone.color}30` }}
        >
          <ZoneIcon className="w-3 h-3" />
          {zone.shortLabel}
        </div>

        {typeConfig && (
          <div
            className="absolute bottom-3 right-3 w-2.5 h-2.5 rounded-full ring-2 ring-zinc-950/60"
            style={{ backgroundColor: typeConfig.color }}
            title={projectType}
          />
        )}

        {/* Select button */}
        {!isInStack && onSelect && (
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(project); }}
            className={cn(
              'absolute top-3 right-12 p-1.5 rounded-lg backdrop-blur-sm transition-all z-10',
              isSelected
                ? 'bg-purple-500 text-white opacity-100'
                : 'bg-zinc-950/50 text-zinc-400 hover:text-white opacity-0 group-hover:opacity-100'
            )}
            title={isSelected ? 'Deselect' : 'Select for fork/collect'}
          >
            <CheckSquare className="w-3.5 h-3.5" />
          </button>
        )}

        {!isInStack && (
          <button
            onClick={(e) => { e.stopPropagation(); onViewOnMap(project); }}
            className="absolute top-3 right-3 p-1.5 rounded-lg bg-zinc-950/50 backdrop-blur-sm opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-white transition-all"
            title="View on Map"
          >
            <MapIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="text-[15px] font-semibold text-white group-hover:text-purple-300 transition-colors line-clamp-1 leading-snug">
          {project.name}
        </h3>
        {project.description && (
          <p className="text-[13px] text-zinc-500 line-clamp-2 leading-relaxed mt-1.5">{project.description}</p>
        )}

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {tags.slice(0, 3).map((tag, i) => (
              <span key={i} className="px-2 py-0.5 bg-zinc-800/70 rounded-md text-[11px] text-zinc-500 font-medium">{tag}</span>
            ))}
            {tags.length > 3 && <span className="text-[11px] text-zinc-600 self-center">+{tags.length - 3}</span>}
          </div>
        )}

        <div className="flex-1" />
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800/40">
          <span className="text-[12px] text-zinc-500 truncate max-w-[140px]">{getCreator(project)}</span>
          <div className="flex items-center gap-3 text-[12px] text-zinc-600">
            {(project.fork_count || 0) > 0 && <span className="flex items-center gap-1"><GitFork className="w-3 h-3" />{fmt(project.fork_count)}</span>}
            {(project.parent_project_id || (project.parent_project_ids && project.parent_project_ids.length > 0)) && (
              <span className="flex items-center gap-1 text-purple-500/70"><Layers className="w-3 h-3" />lineage</span>
            )}
            <span>{timeAgo(project.created_at || project.createdAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MOBILE COMPACT CARD
// ============================================================================

const MobileCompactCard = ({ project, onView, onViewOnMap, onSelect, isSelected, crossZones, lineageDepth }) => {
  const zone = getZoneForProject(project);
  const coverImage = getCover(project);
  const ZoneIcon = ZONE_ICONS[zone.id] || Package;
  const projectType = project.project_type || project.projectType || 'digital';
  const typeConfig = PROJECT_TYPES[projectType];
  const hasCrossZone = crossZones && crossZones.length > 0;
  const hasLineage = lineageDepth > 1;

  return (
    <div
      data-project-id={project.id}
      onClick={() => onView(project)}
      className={cn(
        'group relative bg-zinc-900/60 border rounded-xl overflow-hidden cursor-pointer transition-all duration-200 active:scale-[0.97]',
        isSelected ? 'border-purple-500 ring-2 ring-purple-500/30' : 'border-zinc-800/50'
      )}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        {onSelect && (
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(project); }}
            className={cn(
              'absolute top-2 right-2 p-1 rounded-md z-10 transition-all',
              isSelected
                ? 'bg-purple-500 text-white opacity-100'
                : 'bg-zinc-950/50 text-zinc-400 opacity-0 group-hover:opacity-100 active:opacity-100'
            )}
          >
            <CheckSquare className="w-3 h-3" />
          </button>
        )}
        {coverImage ? (
          <img src={coverImage} alt={project.name} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${zone.color}10, transparent)` }}>
            <span className="text-3xl font-bold select-none" style={{ color: `${zone.color}15` }}>{project.name?.[0]?.toUpperCase() || '?'}</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/70 via-transparent to-transparent" />

        <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-semibold backdrop-blur-md" style={{ backgroundColor: `${zone.color}30`, color: zone.color }}>
          <ZoneIcon className="w-2.5 h-2.5" />
          {zone.shortLabel}
        </div>

        {typeConfig && (
          <div className="absolute bottom-2 right-2 w-2 h-2 rounded-full ring-1 ring-zinc-950/60" style={{ backgroundColor: typeConfig.color }} />
        )}

        {hasCrossZone && (
          <div className="absolute top-1.5 left-1.5">
            <CrossZoneBadge crossZones={crossZones} className="!text-[8px] !px-1.5 !py-px" />
          </div>
        )}
      </div>

      <div className="p-2.5">
        <h3 className="text-[12px] font-semibold text-white line-clamp-2 leading-tight">{project.name}</h3>
        <div className="flex items-center gap-1.5 mt-1.5 text-[10px] text-zinc-500">
          <span className="truncate max-w-[70px]">{getCreator(project)}</span>
          {hasLineage && (
            <span className="flex items-center gap-0.5 text-purple-400/70"><Layers className="w-2.5 h-2.5" />{lineageDepth}</span>
          )}
          {(project.fork_count || 0) > 0 && (
            <span className="flex items-center gap-0.5"><GitFork className="w-2.5 h-2.5" />{fmt(project.fork_count)}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ADAPTIVE CARD
// ============================================================================

const ProjectCard = ({ project, onView, onViewOnMap, onSelect, isSelected, isInStack = false, crossZones, lineageDepth, isMobileGrid = false }) => {
  if (isMobileGrid && !isInStack) {
    return <MobileCompactCard project={project} onView={onView} onViewOnMap={onViewOnMap} onSelect={onSelect} isSelected={isSelected} crossZones={crossZones} lineageDepth={lineageDepth} />;
  }
  if (getCover(project)) {
    return <VisualCard project={project} onView={onView} onViewOnMap={onViewOnMap} onSelect={onSelect} isSelected={isSelected} isInStack={isInStack} crossZones={crossZones} lineageDepth={lineageDepth} />;
  }
  return <TextCard project={project} onView={onView} onViewOnMap={onViewOnMap} onSelect={onSelect} isSelected={isSelected} isInStack={isInStack} />;
};

// ============================================================================
// FORK STACK
// ============================================================================

const ForkStack = ({ chain, onView, onViewOnMap, onSelect, isSelectedFn, crossZones, lineageDepth }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const containerRef = useRef(null);

  const total = chain.length;
  const activeProject = chain[activeIndex];

  const goNext = useCallback(() => setActiveIndex(i => Math.min(i + 1, total - 1)), [total]);
  const goPrev = useCallback(() => setActiveIndex(i => Math.max(i - 1, 0)), []);

  const handleTouchStart = useCallback((e) => { touchStartX.current = e.touches[0].clientX; touchStartY.current = e.touches[0].clientY; }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null; touchStartY.current = null;
    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
      if (deltaX < 0) goNext(); else goPrev();
    }
  }, [goNext, goPrev]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
  }, [goNext, goPrev]);

  const behindCount = Math.min(total - 1 - activeIndex, 2);

  return (
    <div ref={containerRef} data-project-id={chain[0]?.id} className="relative" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} onKeyDown={handleKeyDown} tabIndex={0} style={{ outline: 'none' }}>
      {behindCount >= 2 && <div className="absolute inset-x-0 top-0 h-full rounded-2xl border border-zinc-800/30 bg-zinc-900/20 pointer-events-none" style={{ transform: 'translateY(8px) scale(0.96)', zIndex: 0 }} />}
      {behindCount >= 1 && <div className="absolute inset-x-0 top-0 h-full rounded-2xl border border-zinc-800/40 bg-zinc-900/30 pointer-events-none" style={{ transform: 'translateY(4px) scale(0.98)', zIndex: 1 }} />}

      <div className="relative overflow-hidden rounded-2xl" style={{ zIndex: 2, height: '340px' }}>
        <div className="absolute inset-0 overflow-hidden">
          <ProjectCard project={activeProject} onView={onView} onViewOnMap={onViewOnMap} onSelect={onSelect} isSelected={isSelectedFn?.(activeProject.id)} isInStack={true} />
        </div>

        <div className="absolute bottom-0 inset-x-0 z-10">
          <div className="flex items-center justify-between px-3 py-2.5 bg-zinc-950/90 backdrop-blur-md border-t border-zinc-800/50 rounded-b-2xl">
            <button onClick={(e) => { e.stopPropagation(); goPrev(); }} disabled={activeIndex === 0} className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 disabled:opacity-20 disabled:cursor-not-allowed transition-colors">
              <ArrowLeft className="w-4 h-4" />
            </button>

            <div className="flex flex-col items-center gap-1.5">
              <div className="flex items-center gap-1">
                {total <= 8 ? (
                  chain.map((_, i) => (
                    <button key={i} onClick={(e) => { e.stopPropagation(); setActiveIndex(i); }}
                      className={cn('rounded-full transition-all', i === activeIndex ? 'w-5 h-1.5 bg-purple-400' : 'w-1.5 h-1.5 bg-zinc-600 hover:bg-zinc-500')} />
                  ))
                ) : (
                  <span className="text-[11px] text-zinc-400 font-medium tabular-nums">{activeIndex + 1} / {total}</span>
                )}
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                <GitFork className="w-3 h-3" />
                {activeIndex === 0 ? (
                  <span>Original &middot; {total - 1} fork{total - 1 !== 1 ? 's' : ''} in lineage</span>
                ) : (
                  <span>Fork {activeIndex} of {total - 1}{activeProject.author?.username && (<> &middot; by <span className="text-zinc-400">{getCreator(activeProject)}</span></>)}</span>
                )}
              </div>
            </div>

            <button onClick={(e) => { e.stopPropagation(); goNext(); }} disabled={activeIndex === total - 1} className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 disabled:opacity-20 disabled:cursor-not-allowed transition-colors">
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <button onClick={(e) => { e.stopPropagation(); onViewOnMap(activeProject); }} className="absolute top-3 right-3 z-10 p-1.5 rounded-lg bg-zinc-950/50 backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:opacity-100 text-zinc-400 hover:text-white transition-all" title="View on Map">
          <MapIcon className="w-3.5 h-3.5" />
        </button>

        {crossZones && crossZones.length > 0 && activeIndex === 0 && (
          <div className="absolute top-3 left-3 z-10"><CrossZoneBadge crossZones={crossZones} /></div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// FEED ITEM
// ============================================================================

const FeedItem = ({ stack, onView, onViewOnMap, onSelect, isSelectedFn, isMobileGrid = false, dimmed = false, highlighted = false }) => {
  const content = stack.hasStack
    ? <ForkStack chain={stack.chain} onView={onView} onViewOnMap={onViewOnMap} onSelect={onSelect} isSelectedFn={isSelectedFn} crossZones={stack.crossZones} lineageDepth={stack.lineageDepth} />
    : <ProjectCard project={stack.root} onView={onView} onViewOnMap={onViewOnMap} onSelect={onSelect} isSelected={isSelectedFn?.(stack.root.id)} crossZones={stack.crossZones} lineageDepth={stack.lineageDepth} isMobileGrid={isMobileGrid} />;

  if (dimmed) {
    return <div className="transition-all duration-300" style={{ opacity: 0.15, filter: 'grayscale(0.5) brightness(0.7)', pointerEvents: 'auto' }}>{content}</div>;
  }
  if (highlighted) {
    const zone = getZoneForProject(stack.root);
    const color = zone?.color || '#a855f7';
    return (
      <div className="transition-all duration-300 rounded-2xl" style={{ boxShadow: `0 0 20px ${color}30, 0 0 40px ${color}15, inset 0 0 1px ${color}20`, outline: `2px solid ${color}50`, outlineOffset: '1px', transform: 'scale(1.01)' }}>
        {content}
      </div>
    );
  }
  return content;
};

// ============================================================================
// USER & CHALLENGE CARDS
// ============================================================================

const UserCard = ({ user, onView }) => {
  const initials = (user.username || '?').charAt(0).toUpperCase();
  const projectCount = user.project_count || user.projectCount || 0;
  return (
    <button onClick={() => onView(user)} className="flex items-center gap-3 p-3 bg-zinc-900/50 border border-zinc-800/50 rounded-xl text-left transition-all hover:border-zinc-700/60 hover:bg-zinc-900/70 w-full group">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
        {(user.profile_picture_url || user.profilePictureUrl) ? (
          <img src={user.profile_picture_url || user.profilePictureUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-white font-semibold text-sm">{initials}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate group-hover:text-purple-300 transition-colors">{user.username}</p>
        <p className="text-[11px] text-zinc-500">{projectCount} project{projectCount !== 1 ? 's' : ''}</p>
      </div>
      <ArrowRight className="w-4 h-4 text-zinc-700 group-hover:text-zinc-400 transition-colors flex-shrink-0" />
    </button>
  );
};

const ChallengeCard = ({ challenge, onOpen }) => {
  const responseCount = challenge.response_count || challenge.responseCount || 0;
  const title = challenge.challenge_title || challenge.challengeTitle || challenge.title || challenge.prompt || 'Untitled Challenge';
  const isUnanswered = responseCount === 0;
  const skills = challenge.skills_needed || challenge.skillsNeeded || [];
  const project = challenge.project;
  const projectImage = project?.image_url || project?.imageUrl || project?.cover_image_url;
  const projectName = project?.name;
  const projectZone = project ? getZoneForProject(project) : null;
  const ZoneIcon = projectZone ? (ZONE_ICONS[projectZone.id] || Package) : Target;

  return (
    <button onClick={() => onOpen(challenge)} className={cn('group relative flex gap-0 bg-zinc-900/50 border rounded-xl text-left transition-all duration-200 w-full overflow-hidden', 'hover:shadow-lg hover:shadow-black/30 hover:-translate-y-px', isUnanswered ? 'border-amber-500/25 hover:border-amber-500/50' : 'border-zinc-800/50 hover:border-zinc-700/60')}>
      <div className="w-16 flex-shrink-0 relative overflow-hidden bg-zinc-800/60">
        {projectImage ? (
          <img src={projectImage} alt={projectName || ''} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: projectZone ? `linear-gradient(135deg, ${projectZone.color}18, transparent)` : undefined }}>
            <ZoneIcon className="w-5 h-5" style={{ color: projectZone?.color || '#f59e0b', opacity: 0.5 }} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-zinc-900/60" />
      </div>

      <div className="flex-1 min-w-0 p-3 pl-3.5">
        <div className="flex items-start gap-2">
          {projectZone && (
            <span className="mt-px flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold flex-shrink-0" style={{ backgroundColor: `${projectZone.color}18`, color: projectZone.color }}>
              <ZoneIcon className="w-2.5 h-2.5" />{projectZone.shortLabel}
            </span>
          )}
          {isUnanswered && (
            <span className="mt-px flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20 flex-shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />Open
            </span>
          )}
        </div>
        <p className="text-[13px] font-semibold text-white group-hover:text-amber-300 transition-colors line-clamp-2 mt-1.5 leading-snug">{title}</p>
        {skills.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {skills.slice(0, 3).map((s, i) => (<span key={i} className="px-1.5 py-px rounded text-[10px] text-amber-300/60 bg-amber-500/[0.07] font-medium">{s}</span>))}
            {skills.length > 3 && <span className="text-[10px] text-zinc-600">+{skills.length - 3}</span>}
          </div>
        )}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2 text-[11px] text-zinc-500">
            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{responseCount} response{responseCount !== 1 ? 's' : ''}</span>
            {projectName && (<><span className="text-zinc-700">&middot;</span><span className="truncate max-w-[90px] text-zinc-600">{projectName}</span></>)}
          </div>
          <span className="text-[11px] text-zinc-600">{timeAgo(challenge.created_at || challenge.createdAt)}</span>
        </div>
      </div>
    </button>
  );
};

// ============================================================================
// CHALLENGE DETAIL MINI-OVERLAY
// ============================================================================

const ChallengeDetailMiniOverlay = ({ challenge, onClose, onViewOnMap }) => {
  const overlayRef = useRef(null);
  const title = challenge.challenge_title || challenge.challengeTitle || challenge.title || 'Challenge';
  const description = challenge.challenge_description || challenge.challengeDescription || challenge.description;
  const responseCount = challenge.response_count || challenge.responseCount || 0;
  const skills = challenge.skills_needed || challenge.skillsNeeded || [];
  const project = challenge.project;
  const projectImage = project?.image_url || project?.imageUrl;
  const projectName = project?.name;
  const projectZone = project ? getZoneForProject(project) : null;
  const requester = challenge.requester;
  const isUnanswered = responseCount === 0;

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    const handleClick = (e) => { if (overlayRef.current && !overlayRef.current.contains(e.target)) onClose(); };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => { document.removeEventListener('keydown', handleKey); document.removeEventListener('mousedown', handleClick); };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}>
      <div ref={overlayRef} className="relative w-full max-w-md bg-zinc-900 border border-zinc-800/80 rounded-2xl overflow-hidden shadow-2xl" style={{ boxShadow: '0 25px 60px -12px rgba(0,0,0,0.6), 0 0 50px -20px rgba(245,158,11,0.12)' }}>
        <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, #f59e0b80, #f59e0b10)' }} />
        <button onClick={onClose} className="absolute top-3.5 right-3.5 z-10 p-1.5 rounded-full bg-zinc-800/80 hover:bg-zinc-700 transition-colors"><X className="w-3.5 h-3.5 text-zinc-400" /></button>
        <div className="px-5 pt-5 pb-3">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl overflow-hidden bg-zinc-800 flex-shrink-0 border border-zinc-700/40">
              {projectImage ? (<img src={projectImage} alt={projectName || ''} className="w-full h-full object-cover" />) : (
                <div className="w-full h-full flex items-center justify-center" style={{ background: projectZone ? `linear-gradient(135deg, ${projectZone.color}20, transparent)` : undefined }}><Target className="w-5 h-5 text-amber-400/40" /></div>
              )}
            </div>
            <div className="flex-1 min-w-0 pr-6">
              <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/20">Challenge</span>
                {isUnanswered ? (<span className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-500/10 text-emerald-400"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Needs a response</span>) : (<span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-zinc-700/50 text-zinc-400">{responseCount} response{responseCount !== 1 ? 's' : ''}</span>)}
                {projectZone && (<span className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold" style={{ backgroundColor: `${projectZone.color}18`, color: projectZone.color }}>{projectZone.shortLabel}</span>)}
              </div>
              <h2 className="text-[15px] font-bold text-white leading-snug line-clamp-3">{title}</h2>
              {requester?.username && (<p className="text-[11px] text-zinc-500 mt-1">by <span className="text-zinc-300">{requester.username}</span>{' \u00b7 '}{timeAgo(challenge.created_at || challenge.createdAt)}</p>)}
            </div>
          </div>
        </div>
        <div className="px-5 pb-4 space-y-3">
          {description && (<div className="p-3 rounded-xl bg-amber-500/[0.04] border border-amber-500/10"><p className="text-[13px] text-zinc-300 leading-relaxed whitespace-pre-wrap line-clamp-5">{description}</p></div>)}
          {skills.length > 0 && (<div><p className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold mb-1.5">Skills needed</p><div className="flex flex-wrap gap-1.5">{skills.map((s, i) => (<span key={i} className="px-2 py-0.5 rounded-md bg-zinc-800/50 text-[11px] text-amber-300/80 font-medium border border-amber-500/10">{s}</span>))}</div></div>)}
          {projectName && (<div className="flex items-center gap-2 text-[12px] text-zinc-500 pt-1"><FileText className="w-3 h-3 flex-shrink-0" /><span>Attached to <span className="text-zinc-300 font-medium">{projectName}</span></span></div>)}
        </div>
        <div className="px-5 py-3.5 border-t border-zinc-800/50 bg-zinc-900/60 flex items-center gap-2.5">
          <button onClick={() => onViewOnMap(challenge)} className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-amber-500/10 hover:bg-amber-500/18 border border-amber-500/25 hover:border-amber-500/40 text-amber-300 text-[13px] font-semibold transition-all"><MapIcon className="w-3.5 h-3.5" />View on Map</button>
          <button onClick={onClose} className="py-2 px-3 rounded-xl bg-zinc-800/60 hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 text-[13px] font-medium transition-colors">Close</button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// LOADING & EMPTY
// ============================================================================

const VisualSkeleton = () => (<div className="bg-zinc-900/50 border border-zinc-800/50 rounded-2xl overflow-hidden animate-pulse"><div className="aspect-[16/10] bg-zinc-800/30" /><div className="p-4 space-y-2.5"><div className="h-4 bg-zinc-800/40 rounded-md w-3/4" /><div className="h-3 bg-zinc-800/20 rounded-md w-full" /><div className="h-3 bg-zinc-800/20 rounded-md w-1/2" /></div></div>);
const TextSkeleton = () => (<div className="bg-zinc-900/50 border border-zinc-800/50 rounded-2xl overflow-hidden animate-pulse"><div className="h-1 bg-zinc-800/30" /><div className="p-5 space-y-3"><div className="flex gap-2"><div className="h-6 w-16 bg-zinc-800/40 rounded-lg" /><div className="h-6 w-12 bg-zinc-800/30 rounded" /></div><div className="h-4 bg-zinc-800/40 rounded-md w-2/3" /><div className="h-3 bg-zinc-800/20 rounded-md w-full" /><div className="h-3 bg-zinc-800/20 rounded-md w-3/4" /></div></div>);
const LoadingGrid = () => (<div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5"><VisualSkeleton /><TextSkeleton /><VisualSkeleton /><TextSkeleton /><VisualSkeleton /><TextSkeleton /></div>);

const EmptyState = ({ searchQuery, hasFilters, onClear }) => (
  <div className="flex flex-col items-center justify-center py-24">
    <div className="w-16 h-16 rounded-2xl bg-zinc-800/40 flex items-center justify-center mb-4"><Search className="w-7 h-7 text-zinc-600" /></div>
    <h3 className="text-base font-semibold text-zinc-300 mb-1">No projects found</h3>
    <p className="text-sm text-zinc-500 text-center max-w-sm">{searchQuery ? `Nothing matches "${searchQuery}". Try different keywords.` : 'No projects match your filters.'}</p>
    {hasFilters && (<button onClick={onClear} className="mt-4 px-4 py-2 rounded-lg text-sm font-medium text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 transition-colors">Clear all filters</button>)}
  </div>
);

// ============================================================================
// EXPLORE PAGE
// ============================================================================

const ExplorePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollRef = useRef(null);
  const gridRef = useRef(null);

  // Global tray state
  const tray = useTray();
  const { branches, scrubber } = tray;
  const focus = tray.focus || { focusedProjectIds: null, focusProject: () => {}, clearFocus: () => {} };

  // Feed multi-select
  const feedSel = useFeedSelection();
  const branchHighlightIds = useMemo(() => {
    if (!branches.activeBranch) return null;
    return new Set(branches.activeBranch.curatedProjectIds || []);
  }, [branches.activeBranch]);

  const activeHighlightIds = useMemo(() => {
    if (focus.focusedProjectIds) return focus.focusedProjectIds;
    return branchHighlightIds;
  }, [focus.focusedProjectIds, branchHighlightIds]);

  // Read unified search from PlatformContext
  const platform = usePlatform();
  const { search: platformSearch, filters: platformFilters, lineage: platformLineage, allProjects: globalProjects } = platform;
  const searchQuery = platformSearch.debouncedQuery;

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Open Challenges feed state
  const [openChallenges, setOpenChallenges] = useState([]);
  const [showAllChallenges, setShowAllChallenges] = useState(false);
  const [challengeZoneFilter, setChallengeZoneFilter] = useState('');
  const [activeChallengeOverlay, setActiveChallengeOverlay] = useState(null);

  // In-page project detail overlay
  const [activeProjectOverlay, setActiveProjectOverlay] = useState(null);
  const [exploreCuratedBranches, setExploreCuratedBranches] = useState([]);
  const [challengeProject, setChallengeProject] = useState(null); // project to create challenge on

  useEffect(() => {
    if (!activeProjectOverlay) { setExploreCuratedBranches([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await (projectsAPI.getContainingBranches
          ? projectsAPI.getContainingBranches(activeProjectOverlay.id)
          : api.get(`/projects/${activeProjectOverlay.id}/containing-branches`));
        if (!cancelled) setExploreCuratedBranches(res.data?.branches || []);
      } catch (err) {
        console.warn('Could not fetch containing branches:', err.message);
        if (!cancelled) setExploreCuratedBranches([]);
      }
    })();
    return () => { cancelled = true; };
  }, [activeProjectOverlay?.id]);

  const fetchOpenChallenges = useCallback(async () => {
    try {
      const params = { limit: 30 };
      if (challengeZoneFilter) params.zone = challengeZoneFilter;
      const res = await api.get('/challenges', { params });
      const all = res.data.challenges || res.data || [];
      all.sort((a, b) => {
        const aResp = a.response_count || a.responseCount || 0;
        const bResp = b.response_count || b.responseCount || 0;
        if (aResp === 0 && bResp > 0) return -1;
        if (bResp === 0 && aResp > 0) return 1;
        return new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0);
      });
      setOpenChallenges(all);
    } catch { setOpenChallenges([]); }
  }, [challengeZoneFilter]);

  useEffect(() => { fetchOpenChallenges(); setShowAllChallenges(false); }, [fetchOpenChallenges]);

  const unansweredCount = useMemo(() =>
    openChallenges.filter(c => (c.response_count || c.responseCount || 0) === 0).length
  , [openChallenges]);

  const visibleChallenges = showAllChallenges ? openChallenges : openChallenges.slice(0, 4);

  // ── Data: apply platform filters to global project cache ──────────
  // No more local fetchProjects — we filter the shared cache so that
  // filters set on the map page persist when navigating here.
  useEffect(() => {
    setLoading(true);
    const starredSet = tray.starred?.starredIds || [];
    const filtered = platformFilters.applyFilters(globalProjects, {
      userId: user?.id,
      starredIds: starredSet,
    });
    setProjects(filtered);
    setTotalResults(filtered.length);
    setPage(1);
    setLoading(false);
  }, [globalProjects, platformFilters, user?.id, tray.starred?.starredIds]);

  // Sync URL params with platform filter state (read-only reflection)
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (platformFilters.activeZones.length > 0) params.set('zone', platformFilters.activeZones[0]);
    if (platformFilters.activeTypeFilters.length > 0) params.set('type', platformFilters.activeTypeFilters[0]);
    if (platformFilters.sortBy !== 'newest') params.set('sort', platformFilters.sortBy);
    setSearchParams(params, { replace: true });
  }, [searchQuery, platformFilters.activeZones, platformFilters.activeTypeFilters, platformFilters.sortBy, setSearchParams]);

  const stacks = useMemo(() => buildLineageStacks(projects), [projects]);

  const paginatedStacks = useMemo(() => {
    const totalStackPages = Math.ceil(stacks.length / PER_PAGE);
    setTotalPages(totalStackPages);
    const start = (page - 1) * PER_PAGE;
    return stacks.slice(start, start + PER_PAGE);
  }, [stacks, page]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); }, [page]);

  // Build lineage chain using the shared platform lineage system
  const handleViewProject = useCallback((project) => {
    setActiveProjectOverlay(project);
    focus.clearFocus();

    const chain = platformLineage.buildLineageChain(project);
    if (chain.length >= 1) {
      const enriched = chain.map(p => {
        if (p.zone) return p;
        try { const z = getProjectZone(p); return { ...p, zone: z.id }; } catch { return { ...p, zone: 'default' }; }
      });
      scrubber.openScrubber(enriched);
    }
  }, [platformLineage, scrubber, focus]);

  const handleViewOnMap = (project) => navigate(`/dashboard?focus=${project.id}`);
  const handleViewUser = (u) => navigate(`/profile/${u.id}`);
  const handleViewChallenge = (c) => setActiveChallengeOverlay(c);
  const handleOpenChallenge = (c) => setActiveChallengeOverlay(c);
  const handleCloseChallengeOverlay = () => setActiveChallengeOverlay(null);
  const handleChallengeViewOnMap = (c) => {
    const pid = c.project_id || c.projectId || c.project?.id;
    const cid = c.id;
    setActiveChallengeOverlay(null);
    if (pid) navigate(`/dashboard?project=${pid}&challenge=${cid}`);
    else navigate(`/dashboard?challenge=${cid}`);
  };

  const clearAllFilters = () => {
    platformSearch.clear();
    platformFilters.clearAllFilters();
    setPage(1);
  };

  const hasActiveFilters = searchQuery || platformFilters.activeFilterCount > 0;

  const stackedCount = stacks.filter(s => s.hasStack).length;

  return (
    <div ref={scrollRef} className="min-h-screen bg-zinc-950 overflow-y-auto">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">

        {/* ═══ UNIFIED SEARCH + FILTER BAR ═══ */}
        <div className="sticky top-0 z-30 border-b border-zinc-800/30">
          <UnifiedSearchBar
            variant="page"
            onResultClick={(project) => setActiveProjectOverlay(project)}
          />
        </div>

        {/* ═══ SEARCH RESULTS: People + Challenges ═══ */}
        {/* Now powered by PlatformContext's enriched search — same data as the dropdown */}
        {searchQuery && platformSearch.userResults?.length > 0 && (
          <div className="mt-6 mb-4">
            <h2 className="text-[13px] font-semibold text-zinc-400 uppercase tracking-wider mb-2.5 flex items-center gap-2">
              <Users className="w-3.5 h-3.5 text-pink-400" />People
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {platformSearch.userResults.map(u => <UserCard key={u.id} user={u} onView={handleViewUser} />)}
            </div>
          </div>
        )}
        {searchQuery && platformSearch.challengeResults?.length > 0 && (
          <div className="mb-4">
            <h2 className="text-[13px] font-semibold text-zinc-400 uppercase tracking-wider mb-2.5 flex items-center gap-2">
              <Target className="w-3.5 h-3.5 text-amber-400" />Challenges
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {platformSearch.challengeResults.map(c => <ChallengeCard key={c.id} challenge={c} onOpen={handleViewChallenge} />)}
            </div>
          </div>
        )}

        {/* === SURPRISING CONNECTION SPOTLIGHT === */}
        {!searchQuery && !loading && stacks.length > 0 && (
          <SurprisingConnectionSpotlight stacks={stacks} onView={handleViewProject} onViewOnMap={handleViewOnMap} />
        )}

        {/* ═══ OPEN CHALLENGES ═══ */}
        {!searchQuery && (openChallenges.length > 0 || challengeZoneFilter) && (
          <div className="mt-6 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[13px] font-semibold text-zinc-400 uppercase tracking-wider flex items-center gap-2">
                <Target className="w-3.5 h-3.5 text-amber-400" />
                Open Challenges
                {unansweredCount > 0 && (
                  <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-bold normal-case tracking-normal">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                    {unansweredCount} need{unansweredCount === 1 ? 's' : ''} a response
                  </span>
                )}
              </h2>
              {openChallenges.length > 4 && (
                <button onClick={() => setShowAllChallenges(!showAllChallenges)} className="text-[12px] text-zinc-500 hover:text-zinc-300 transition-colors">
                  {showAllChallenges ? 'Show less' : `Show all ${openChallenges.length}`}
                </button>
              )}
            </div>

            <div className="flex items-center gap-1.5 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1 mb-3">
              <button onClick={() => setChallengeZoneFilter('')}
                className={cn('flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all flex-shrink-0',
                  !challengeZoneFilter ? 'bg-amber-500/15 text-amber-300 border border-amber-500/20' : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/40')}>
                All zones
              </button>
              {ZONE_LIST.map(zone => {
                const Icon = ZONE_ICONS[zone.id] || Package;
                const isActive = challengeZoneFilter === zone.id;
                return (
                  <button key={zone.id} onClick={() => setChallengeZoneFilter(isActive ? '' : zone.id)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all flex-shrink-0"
                    style={isActive ? { backgroundColor: `${zone.color}18`, color: zone.color, border: `1px solid ${zone.color}30` } : { color: '#71717a' }}>
                    <Icon className="w-3 h-3" />{zone.shortLabel}
                  </button>
                );
              })}
            </div>

            {openChallenges.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-zinc-600 text-[13px]">
                <Target className="w-6 h-6 mb-2 opacity-40" />
                No open challenges in this zone yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {visibleChallenges.map(c => (<ChallengeCard key={c.id} challenge={c} onOpen={handleOpenChallenge} />))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-6 mb-4">
          <h2 className="text-[13px] font-semibold text-zinc-400 uppercase tracking-wider flex items-center gap-2">
            Projects
            <span className="text-zinc-600 font-normal normal-case">
              ({totalResults}{stackedCount > 0 && ` \u00b7 ${stackedCount} with lineage`})
            </span>
          </h2>
        </div>

        {/* ═══ PROJECT GRID ═══ */}
        {loading ? (
          <LoadingGrid />
        ) : paginatedStacks.length === 0 ? (
          <EmptyState searchQuery={searchQuery} hasFilters={hasActiveFilters} onClear={clearAllFilters} />
        ) : (
          <>
            <div className="relative">
              <ConnectionLines projects={projects} gridRef={gridRef} stacks={paginatedStacks} />
              <div ref={gridRef} className="relative grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5" style={{ zIndex: 2 }}>
                {paginatedStacks.map(stack => {
                  const isInHighlight = activeHighlightIds !== null && (() => {
                    if (activeHighlightIds.has(stack.root.id)) return true;
                    if (stack.chain) return stack.chain.some(p => activeHighlightIds.has(p.id));
                    return false;
                  })();
                  const isDimmed = activeHighlightIds !== null && !isInHighlight;
                  const isHighlighted = activeHighlightIds !== null && isInHighlight;

                  return (
                    <FeedItem key={stack.root.id} stack={stack} onView={handleViewProject} onViewOnMap={handleViewOnMap} onSelect={feedSel.toggle} isSelectedFn={feedSel.isSelected} isMobileGrid={true} dimmed={isDimmed} highlighted={isHighlighted} />
                  );
                })}
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-1.5 py-10">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (page <= 3) pageNum = i + 1;
                  else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = page - 2 + i;
                  return (
                    <button key={pageNum} onClick={() => setPage(pageNum)} className={cn('w-9 h-9 rounded-lg text-sm font-medium transition-colors', page === pageNum ? 'bg-purple-600 text-white' : 'text-zinc-500 hover:text-white hover:bg-zinc-800')}>{pageNum}</button>
                  );
                })}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"><ChevronRight className="w-4 h-4" /></button>
              </div>
            )}
          </>
        )}

        <div className="h-8" />
      </div>

      {/* Challenge Detail Overlay */}
      {activeChallengeOverlay && (
        <ChallengeDetailMiniOverlay challenge={activeChallengeOverlay} onClose={handleCloseChallengeOverlay} onViewOnMap={handleChallengeViewOnMap} />
      )}

      {/* Project Detail Overlay */}
      {activeProjectOverlay && (
        <OverlayErrorBoundary onClose={() => setActiveProjectOverlay(null)} name="Project Details" resetKey={activeProjectOverlay?.id}>
          <ProjectDetailOverlay
            project={activeProjectOverlay}
            parentProjects={projects.filter(p => activeProjectOverlay.parent_project_ids?.includes(p.id) || p.id === activeProjectOverlay.parent_project_id)}
            childProjects={projects.filter(p => p.parent_project_id === activeProjectOverlay.id || (p.parent_project_ids || []).includes(activeProjectOverlay.id))}
            curatedBranches={exploreCuratedBranches}
            onClose={() => setActiveProjectOverlay(null)}
            onFork={() => navigate(`/dashboard?fork=${activeProjectOverlay.id}`)}
            onCreateChallenge={() => {
              setChallengeProject(activeProjectOverlay);
              setActiveProjectOverlay(null);
            }}
            onViewProject={(project) => setActiveProjectOverlay(project)}
            onEdit={() => navigate(`/dashboard?edit=${activeProjectOverlay.id}`)}
            onDelete={async (project) => {
              try { await api.delete(`/projects/${project.id}`); setActiveProjectOverlay(null); setProjects(prev => prev.filter(p => p.id !== project.id)); } catch {}
            }}
          />
        </OverlayErrorBoundary>
      )}

      {/* Create Challenge Overlay — opens in-page instead of navigating to Dashboard */}
      {challengeProject && (
        <OverlayErrorBoundary onClose={() => setChallengeProject(null)} name="Create Challenge" resetKey={challengeProject?.id}>
          <CreateChallengeOverlay
            project={challengeProject}
            onClose={() => setChallengeProject(null)}
            onSuccess={() => {
              setChallengeProject(null);
              // Refresh data so the new challenge appears
              platform.refetchData?.();
            }}
          />
        </OverlayErrorBoundary>
      )}

      {/* Multi-select bar */}
      <FeedSelectionBar {...feedSel} />
    </div>
  );
};

export default ExplorePage;